import {
    getChannel,
    getChannelTags,
    inviteUser,
    postChatRead,
    updateChannel,
} from 'api/socket.request'
import { useCategoryUnreadCount } from 'contexts/CategoryUnreadCountProvider'
import { useTalkPlusClient } from 'contexts/TalkPlusClientContext'
import { ITag } from 'interfaces/tag.interface'
import { ChatStatus, CustomChannel } from 'interfaces/talkplus.interface'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { userState } from 'store/user'
import { Channel, Member } from 'talkplus-sdk'
import customDebounce from 'utils/customDebounce'
import { useResponsive } from 'utils/helper'
import { convertHttpToHttps } from 'utils/lib'
import { protectedServerError } from 'api/serverErrorStatus'

interface ChannelContextType {
    channel: CustomChannel | null
    setChannel: (channel: CustomChannel | null) => void
    channelId: string
    debouncedMarkAsRead: (channelId: string) => void
    statusCompleteHandler: () => Promise<void>
    isPageFocused: boolean
    isTalkplusLoginComplete: boolean
    tablet: boolean
    isGuestPanelOpen: boolean
    handleGuestPanel: (open: boolean) => void
    updateChannelInfo: (newChannel: Channel) => Promise<void>
    isChannelError: boolean
    channelErrorCode: string
    handleUpdateChannelTagList: (tags: ITag[]) => Promise<void>
    getTagList: () => Promise<ITag[]>
    channelErrorHandler: (err: any) => void
    isChannelEntryFirstLoading: boolean
    channelEntryErrorStatus: string
}

const ChannelContext = createContext<ChannelContextType | undefined>(undefined)

export const ChannelProvider = ({ children }: { children: React.ReactNode }) => {
    const { client, isTalkplusLoginComplete, isPageFocused } = useTalkPlusClient()
    const { id } = useParams()
    const user = useRecoilValue(userState)
    const [channel, setChannel] = useState<CustomChannel | null>(null)
    const { tablet } = useResponsive()
    const [isGuestPanelOpen, setIsGuestPanelOpen] = useState<boolean>(!tablet)
    const [searchParams, setSearchParams] = useSearchParams()
    const { debouncedFetchUnreadCounts } = useCategoryUnreadCount()
    const navigate = useNavigate()
    const [channelErrorCode, setChannelErrorCode] = useState<string>('')
    const [channelEntryErrorStatus, setChannelEntryErrorStatus] = useState<string>('')
    const [isChannelError, setIsChannelError] = useState<boolean>(false)
    const [isChannelEntryFirstLoading, setIsChannelEntryFirstLoading] = useState<boolean>(true)

    const channelId = `${user?.userId}_${id}`
    const channelIdx = channel?.idx

    const channelErrorHandler = (err: any) => {
        setIsChannelError(true)

        if ('code' in err) {
            const isServerError = protectedServerError(err.code)
            if (isServerError) {
                // 5xx 서버 에러 발생 시 에러 코드 저장
                setChannelEntryErrorStatus(err.code)
                return
            }
            if (err.code) {
                // 에러 발생 시 에러 코드 저장
                setChannelErrorCode(err.code)
                return
            }
        }
    }

    // 채널 정보 업데이트 함수
    const updateChannelInfo = async (newChannel: Channel) => {
        if (!newChannel.members) return

        try {
            const cmChannelRes = await getChannel(channelId)

            const cmChannel = cmChannelRes.data.data
            const tagList: ITag[] = channel?.tags || []

            const { guest, seller } = extractMembers(newChannel)
            const cmGuest = cmChannel?.members.find((member) => member?.data.role === 'GUEST')

            setChannel({
                id: newChannel.id,
                idx: cmChannel?.idx || 0,
                category: cmChannel?.category || newChannel.category,
                isWithdrawnGuest: Boolean(cmGuest?.isWithdrawn),
                tags: tagList,
                guest: {
                    ...guest,
                    profileImageUrl: convertHttpToHttps(guest?.profileImageUrl),
                    data: {
                        ...guest.data,
                        phone: cmGuest ? cmGuest.data.phone : guest.data?.phone || '',
                    },
                },
                seller,
            })
        } catch (err: any) {
            // 채널 정보 가져오는중 에러가 발생하는 경우 에러 코드 및 에러 상태 설정
            channelErrorHandler(err)
        } finally {
            // 채널 최초 로딩 상태 업데이트
            if (isChannelEntryFirstLoading) {
                setIsChannelEntryFirstLoading(false)
            }
        }
    }

    // 채널 멤버 추출 함수
    const extractMembers = (channel: Channel) => {
        const guest = channel.members?.find((member) => member?.data?.role === 'GUEST') as Member
        const seller = channel.members?.find((member) => member?.data?.role === 'SELLER') as Member

        return { guest, seller }
    }

    // 태그 정보 업데이트 후 로컬 데이터 업데이트
    const handleUpdateChannelTagList = useCallback(async (tags: ITag[]) => {
        // 채널 정보가 없다면 함수 실행하지 않음
        setChannel((prevChannel) => {
            if (!prevChannel) return prevChannel
            return {
                ...prevChannel,
                tags: tags,
            }
        })
    }, [])

    // 채널에 유저가 존재하지 않을 경우 초대 후 채널 업데이트 하는 함수
    const inviteAndUpdateChannel = async (channelId: string, userId: string) => {
        try {
            const response = await inviteUser(channelId, userId)
            const updatedChannel = response.data.data?.channel

            if (updatedChannel) {
                await updateChannelInfo(updatedChannel) // 채널 정보 업데이트
            }
        } catch (err: any) {
            // 유저 채널 초대 실패 시 에러 코드 및 에러 상태 설정
            channelErrorHandler(err)
            // 채널 초대 실패 시 최초 로딩 종료
            setIsChannelEntryFirstLoading(false)
        }
    }

    /*
     * 최초 1회 채널 정보 조회
     * 클릭메이트 DB에는 항상 셀러와 게스트가 존재하지만, 톡플러스에 존재하지 않는 경우 초대 하기 위해 클메 정보가 아닌 톡플러스 채널 정보를 가져옴
     */
    useQuery(
        ['channel', user, id],
        async () => {
            // 톡플러스 채널 정보 불러오기
            return client.getChannel({ channelId })
        },
        {
            onSuccess: async (res) => {
                if (!id) return
                const { channel } = res
                const guest = channel?.members?.find((member) => member?.data?.role === 'GUEST')

                if (guest) {
                    // 게스트 정보가 존재한다면 바로 채널 업데이트
                    await updateChannelInfo(channel)
                } else {
                    // 게스트 정보가 없으면 초대 후 채널 업데이트
                    await inviteAndUpdateChannel(channelId, id)
                }
            },
            onError: async (err: any) => {
                // 2005 - 톡플러스 에러 코드 ( 현재 채널 정보를 조회한 유저가 해당 채널의 멤버가 아닌 경우 )
                // 현재 채널에 접근 권한이 없다면 셀러 자신을 해당 방에 초대
                if ('code' in err && err.code === '2005' && user) {
                    await inviteAndUpdateChannel(channelId, String(user.userId))
                } else {
                    // 톡플러스 채널 정보 가져오는중 에러가 발생하는 경우 에러 코드 및 에러 상태 설정
                    channelErrorHandler(err)
                    // 톡플러스 채널 정보를 가져오는중 에러가 발생했다면 최초 로딩 종료
                    setIsChannelEntryFirstLoading(false)
                }
            },
            enabled: isTalkplusLoginComplete && !!user && !!id,
        }
    )

    // 채널 메세지 읽음 처리 함수
    const markAsRead = useCallback(
        async (channelId: string) => {
            try {
                // 로그인 중이 아니거나, 채널을 보고있지 않다면 메세지를 읽지 않음
                if (!isTalkplusLoginComplete || !isPageFocused) return

                // 클메 서버에서 채널 데이터를 받아옴
                const channelRes = await getChannel(channelId)
                // 채널 데이터가 없거나, 채널 카테고리가 대기일 경우 읽지 않음
                if (!channelRes.data.data || channelRes.data.data?.category === ChatStatus.PENDING)
                    return

                await postChatRead(channelId) // 채팅 읽음 처리 API 호출
                await client.markChannelAsRead({ channelId }) // TalkPlus 클라이언트에서 읽음 처리
                await debouncedFetchUnreadCounts()
            } catch (err) {
                console.log('읽음처리 실패', err)
            }
        },
        [isTalkplusLoginComplete, client, debouncedFetchUnreadCounts, isPageFocused]
    )

    // 채널 메세지 읽음 처리 함수 디바운스 처리 - 1초 디바운스 과도한 API호출 방지
    const debouncedMarkAsRead = useCallback(
        (channelId: string) => {
            customDebounce(markAsRead, 1000)(channelId)
        },
        [markAsRead]
    )

    // 채널 상태 완료 핸들러 - 상담완료 버튼에 쓰임
    const statusCompleteHandler = async () => {
        try {
            await updateChannel(channelId, { category: ChatStatus.COMPLETED })
            debouncedMarkAsRead(channelId)
        } catch (err) {
            console.log(err)
        }
    }

    // 태그 정보를 서버로부터 받아오는 함수
    const getTagList = useCallback(async () => {
        if (channelIdx) {
            const tagListRes = await getChannelTags(channelIdx)
            return tagListRes.data.data || []
        }
        return []
    }, [channelIdx])

    // 게스트 패널 오픈 핸들러
    const handleGuestPanel = (open: boolean) => {
        setIsGuestPanelOpen(open)
        if (!tablet) return
        if (open) {
            // 모바일이며 guest-panel이 오픈 상태라면 파라미터 추가
            setSearchParams({ modal: 'guest-panel' })
        } else {
            removeModalParam()
            navigate(-2)
        }
    }

    // 게스트 패널 파라미터 삭제
    const removeModalParam = () => {
        if (searchParams.get('modal') === 'guest-panel' && tablet) {
            searchParams.delete('modal')
            setSearchParams(searchParams)
        }
    }

    // 파라미터 변경시 modal파라미터가 없고 모바일이라면 게스트 패널 닫기
    useEffect(() => {
        if (searchParams.get('modal') !== 'guest-panel' && tablet) {
            setIsGuestPanelOpen(false)
        }
    }, [searchParams, tablet])

    return (
        <ChannelContext.Provider
            value={{
                channel,
                setChannel,
                channelId,
                debouncedMarkAsRead,
                statusCompleteHandler,
                isPageFocused,
                isTalkplusLoginComplete,
                tablet,
                isGuestPanelOpen,
                handleGuestPanel,
                updateChannelInfo,
                handleUpdateChannelTagList,
                getTagList,
                channelErrorHandler,
                isChannelError,
                channelErrorCode,
                isChannelEntryFirstLoading,
                channelEntryErrorStatus,
            }}
        >
            {children}
        </ChannelContext.Provider>
    )
}

export const useChannel = () => {
    const context = useContext(ChannelContext)
    if (!context) {
        throw new Error('useChannel must be used within a ChannelProvider')
    }
    return context
}
